import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Ban, CheckCircle2, LoaderCircle, X } from 'lucide-react';

import {
  cancelPipefyCardsByIds,
  type PipefyCardsCancelByIdsResult,
} from '../../promotions/services/briefingPromocoesApi';

const MAX_CARD_IDS = 100;

interface CancelPipefyCardsModalProps {
  open: boolean;
  onClose: () => void;
  onCompleted?: (result: PipefyCardsCancelByIdsResult) => void;
}

function parseCardIds(value: string) {
  const tokens = value
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);

  return {
    cardIds: [...new Set(tokens.filter((item) => /^\d+$/.test(item)))],
    invalid: [...new Set(tokens.filter((item) => !/^\d+$/.test(item)))],
  };
}

function getRequestErrorMessage(error: unknown) {
  const code = String(
    (error as { response?: { data?: { error?: string } } })?.response?.data?.error
      || (error instanceof Error ? error.message : ''),
  );
  const messages: Record<string, string> = {
    PIPEFY_CARD_IDS_REQUIRED: 'Informe ao menos um ID de card.',
    PIPEFY_CARD_IDS_INVALID: 'A lista contém IDs inválidos.',
    PIPEFY_CARD_IDS_LIMIT: `Envie no máximo ${MAX_CARD_IDS} IDs por vez.`,
    PIPEFY_BRIEFING_NOT_CONFIGURED: 'A integração com o Pipefy não está configurada.',
    PIPEFY_CANCEL_PHASE_NOT_FOUND: 'A fase de cancelamento não foi encontrada no Pipefy.',
  };

  return messages[code] || 'Não foi possível cancelar os cards. Tente novamente.';
}

function getCardErrorMessage(error: string) {
  if (error === 'PIPEFY_CARD_NOT_FOUND') return 'Card não encontrado';
  if (error === 'PIPEFY_CARD_WRONG_PIPE') return 'Card pertence a outro pipe';
  return 'Não foi possível mover o card';
}

export default function CancelPipefyCardsModal({
  open,
  onClose,
  onCompleted,
}: CancelPipefyCardsModalProps) {
  const [input, setInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [result, setResult] = useState<PipefyCardsCancelByIdsResult | null>(null);
  const parsed = useMemo(() => parseCardIds(input), [input]);

  const rows = useMemo(() => {
    if (!result) return [];
    const moved = new Map(result.moved.map((card) => [card.id, card]));
    const alreadyCancelled = new Map(result.alreadyCancelled.map((card) => [card.id, card]));
    const failed = new Map(result.failed.map((item) => [item.cardId, item.error]));

    return parsed.cardIds.map((cardId) => {
      if (moved.has(cardId)) {
        return { cardId, title: moved.get(cardId)?.title, status: 'moved' as const };
      }
      if (alreadyCancelled.has(cardId)) {
        return {
          cardId,
          title: alreadyCancelled.get(cardId)?.title,
          status: 'already-cancelled' as const,
        };
      }
      return { cardId, status: 'failed' as const, error: failed.get(cardId) || '' };
    });
  }, [parsed.cardIds, result]);

  useEffect(() => {
    if (open) return;
    setInput('');
    setRequestError(null);
    setResult(null);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open, submitting]);

  if (!open) return null;

  const isOverLimit = parsed.cardIds.length > MAX_CARD_IDS;
  const isSubmitDisabled = submitting
    || parsed.cardIds.length === 0
    || parsed.invalid.length > 0
    || isOverLimit;

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    setSubmitting(true);
    setRequestError(null);
    setResult(null);

    try {
      const nextResult = await cancelPipefyCardsByIds(parsed.cardIds);
      setResult(nextResult);
      onCompleted?.(nextResult);
    } catch (error) {
      setRequestError(getRequestErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1080] flex items-center justify-center bg-slate-950/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-pipefy-cards-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 id="cancel-pipefy-cards-title" className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Ban size={18} className="text-rose-600" />
              Cancelar cards por ID
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Os cards informados serão movidos para a fase de cancelados/rejeitados.
            </p>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            disabled={submitting}
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          <label htmlFor="pipefy-card-ids" className="text-sm font-semibold text-slate-700">
            IDs dos cards
          </label>
          <textarea
            id="pipefy-card-ids"
            rows={7}
            autoFocus
            value={input}
            disabled={submitting}
            placeholder={'1422036501\n1422036509\n1421902737'}
            onChange={(event) => {
              setInput(event.target.value);
              setRequestError(null);
              setResult(null);
            }}
            className="mt-2 w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-mono text-sm text-slate-800 outline-none transition placeholder:text-slate-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 disabled:bg-slate-50"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>Separe por linha, espaço, vírgula ou ponto e vírgula.</span>
            <span className={isOverLimit ? 'font-semibold text-rose-600' : ''}>
              {parsed.cardIds.length}/{MAX_CARD_IDS} IDs válidos
            </span>
          </div>

          {parsed.invalid.length > 0 && (
            <div className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertCircle size={17} className="mt-0.5 shrink-0" />
              <span>
                Remova os valores inválidos: {parsed.invalid.slice(0, 5).join(', ')}
                {parsed.invalid.length > 5 ? '…' : ''}
              </span>
            </div>
          )}

          {requestError && (
            <div className="mt-3 flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              <AlertCircle size={17} className="mt-0.5 shrink-0" />
              <span>{requestError}</span>
            </div>
          )}

          {result && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-lg bg-emerald-50 px-2 py-2 text-emerald-700">
                  <strong className="block text-base">{result.moved.length}</strong>
                  Movidos
                </div>
                <div className="rounded-lg bg-blue-50 px-2 py-2 text-blue-700">
                  <strong className="block text-base">{result.alreadyCancelled.length}</strong>
                  Já cancelados
                </div>
                <div className="rounded-lg bg-rose-50 px-2 py-2 text-rose-700">
                  <strong className="block text-base">{result.failed.length}</strong>
                  Falharam
                </div>
              </div>

              <div className="max-h-52 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
                {rows.map((row) => (
                  <div key={row.cardId} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <span className="font-mono font-semibold text-slate-800">{row.cardId}</span>
                      {row.title && <p className="truncate text-xs text-slate-500">{row.title}</p>}
                    </div>
                    {row.status === 'moved' && (
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-700">
                        <CheckCircle2 size={14} /> Movido
                      </span>
                    )}
                    {row.status === 'already-cancelled' && (
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-blue-700">
                        <CheckCircle2 size={14} /> Já cancelado
                      </span>
                    )}
                    {row.status === 'failed' && (
                      <span className="inline-flex shrink-0 items-center gap-1 text-right text-xs font-semibold text-rose-700">
                        <AlertCircle size={14} /> {getCardErrorMessage(row.error)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {result ? 'Fechar' : 'Cancelar'}
          </button>
          {!result && (
            <button
              type="button"
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {submitting ? <LoaderCircle size={16} className="animate-spin" /> : <Ban size={16} />}
              {submitting ? 'Movendo cards…' : 'Mover para cancelado'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
